import {Injectable} from '@angular/core';
import {WebSocketSubject} from 'rxjs/internal/observable/dom/WebSocketSubject';
import {BehaviorSubject, EMPTY, filter, forkJoin, map, Observable, ReplaySubject, retry, Subject, take, timer} from 'rxjs';
import {webSocket} from 'rxjs/webSocket';
import {HttpClient, HttpParams} from '@angular/common/http';
import {SessionService} from '../../core/services/session.service';
import {BaseService} from './_base.service';
import {ConvoId, StepId, TopicId, UUID} from '../models/uuid';
import {UserId} from '../../shared/models/user';
import {
  Authenticate,
  CreateMessage,
  FuncAuthenticate,
  FuncConvoNewStep,
  FuncConvoNewStepGroup,
  FuncConvoStepDeletion,
  FuncConvoStepGroupAddition,
  FuncConvoStepRegen,
  FuncConvoStepUpdate,
  Message,
} from '../models/wss/message';
import {
  Convo,
  NewStepGroupMessage,
  NewStepMessage,
  Step,
  StepConfig,
  StepDeletionMessage,
  StepGroup,
  StepGroupAdditionMessage,
  StepRegenMessage,
  StepUpdateMessage,
} from '../models/convo';
import {FgaType} from '../models/moti-fga/moti-type';
import {MotiGrantee} from '../models/moti-fga/moti-grantee';
import {MotiTimestamp} from '../models/time';
import {TopicConfigConvos} from '../models/topic';
import {Alias, Provider} from '../models/llm/llm';


export const MAX_STEPS_PER_GROUP = 3;


export interface ConvosQuery {
  userId?: UserId;
  topicId?: TopicId;
}


@Injectable({
  providedIn: 'root',
})
export class ConvoService extends BaseService {
  public scrollToBubbleGroup = true;

  private sockets$: { [convoId: ConvoId]: WebSocketSubject<Message> } = {};

  triggerFetchSteps$: Subject<UUID> = new Subject<UUID>();

  /**
   * For tracking when we should close a connection, in case two components are using the same
   * connection at the same time
   */
  activeConvoCounts: { [convoId: ConvoId]: number } = {};
  activeConvos$: { [convoId: ConvoId]: Observable<Observable<Message>> } = {};

  /**
   * Emits an array of IDs of active convos whose connections are being retried due to an
   * unexpected interruption to the connection. Emits anytime the contents of the array change.
   */
  retryingConvoIds$: BehaviorSubject<Set<UUID>> = new BehaviorSubject<Set<UUID>>(new Set<UUID>);

  private messageSubjects$: { [id: UUID]: Subject<Message> } = {};


  constructor(
    private http: HttpClient,
    private sess: SessionService,
  ) {
    super();
    // this.enableHttpLocalhost();
    // this.enableWsLocalhost();
  }


  /**
   * The observable returned will emit complete if the connection succeeded or emit error
   * if it failed. Before emitting complete, it emits an observable which itself emits the
   * convo's messages.
   *
   * @param convoId
   * @private
   */
  private connect$(convoId: ConvoId): Observable<Observable<Message>> {
    const id = convoId;

    return new Observable(subscriber => {
      if (this.sockets$[id] && !this.sockets$[id].closed) {
        subscriber.next(this.messageSubjects$[id]);
        subscriber.complete();
      } else {
        if (!this.messageSubjects$[id]) {
          this.messageSubjects$[id] = new Subject<Message>();
        }

        const socket = this.sockets$[id] = webSocket({
          url: `${this.wsHost}/${id}`,
          openObserver: {
            next: () => {
              // Use bearer token for authentication instead of Firebase token
              const cleanToken = this.sess.getCleanBearerToken();
              if (cleanToken) {
                socket.next(CreateMessage<Authenticate>(FuncAuthenticate, {
                  userId: this.sess.user.ID,
                  token: cleanToken,
                }));

                subscriber.next(this.messageSubjects$[id]);
                subscriber.complete();
              } else {
                console.log('No bearer token available');
                subscriber.error('No authentication token available');
              }
            },
          },
          closeObserver: {
            next: () => {
            },
          },
        });

        socket.pipe(
          retry({
            count: 120,
            delay: () => {
              if (!this.sockets$[id]) {
                // Likely endConvo was called due to lack of authorization. This
                // empty observable will ensure the retries never resume.
                return EMPTY;
              }

              this.emitRetryingConvoId(id);

              return timer(1000);
            },
            resetOnSuccess: true,
          }),
        ).subscribe({
          next: (message) => {
            this.emitNotRetryingConvoId(id);
            this.messageSubjects$[id].next(message);
          },
          error: err => {
            console.log(err, 'err');
            socket.unsubscribe();
          },
          complete: () => {
            this.emitNotRetryingConvoId(id);
            socket.unsubscribe();
          },
        });
      }
    });
  }


  private emitRetryingConvoId(convoId: ConvoId) {
    const retrying = this.retryingConvoIds$.getValue();
    retrying.add(convoId);
    this.retryingConvoIds$.next(retrying);
  }


  private emitNotRetryingConvoId(convoId: UUID) {
    const retrying = this.retryingConvoIds$.getValue();
    retrying.delete(convoId);
    this.retryingConvoIds$.next(retrying);
  }


  /**
   * Returns an observable that emits an observable. The former will only call next
   * one time at most. The latter emits messages as they stream through the WebSocket.
   *
   * This starts a session for a convo that already exists.
   *
   * @see createConvo$ - For actually creating a new convo object
   */
  startConvo$(convoId: UUID): Observable<Observable<Message>> {
    if (this.activeConvos$[convoId]) {
      return this.activeConvos$[convoId];
    }

    return this.activeConvos$[convoId] = new Observable<Observable<Message>>(subscriber => {
      if (!this.activeConvoCounts[convoId]) {
        this.activeConvoCounts[convoId] = 1;
      } else {
        this.activeConvoCounts[convoId]++;
      }

      this.connect$(convoId).subscribe({
        next: (messages$) => {
          subscriber.next(messages$);
        },
        error: err => {
          // Indicates the connection failed
          console.log(err, 'err2');
          subscriber.error(err);
        },
      });

      return () => {
        this.activeConvoCounts[convoId]--;

        if (this.activeConvoCounts[convoId] === 0) {
          const socket = this.sockets$[convoId];
          delete this.sockets$[convoId];
          socket?.complete();

          delete this.activeConvos$[convoId];
        }
      };
    });
  }


  /**
   * To get value out of the result of this function, something needs to be
   * listening to the appropriate convo message observable already.
   */
  sendNewStep(step: Step) {
    this.scrollToBubbleGroup = true;

    if (step.text) {
      step.text = step.text.trim();
    }

    const msg: NewStepMessage = {
      _ws: {
        funcName: FuncConvoNewStep,
      },
      payload: step,
    };

    this.sendMessage(step.convoId, msg);
  }


  sendNewStepGroup(group: StepGroup) {
    this.scrollToBubbleGroup = true;

    if (group.step.text) {
      group.step.text = group.step.text.trim();
    }

    const msg: NewStepGroupMessage = {
      _ws: {
        funcName: FuncConvoNewStepGroup,
      },
      payload: group,
    };

    this.sendMessage(group.step.convoId, msg);
  }


  /**
   * This function assumes data sources have already been added to a convo.
   *
   * This is necessary to associate a data source with a convo in the LLM. To
   * get value out of the result of this function, something needs to be
   * listening to the appropriate convo message observable already.
   */
  sendFileStep(convoId: UUID, dataSourceIds: UUID[]) {
    this.scrollToBubbleGroup = true;

    this.sendNewStep({
      convoId: convoId,
      files: dataSourceIds.map(id => {
        return {
          dataSourceId: id,
        };
      }),
      config: {
        provider: Provider.MANUAL,
        modelAlias: Alias.NONE,
      },
      createdBy: this.sess.user.ID,
    });
  }


  /**
   * An all-in-one method for adding a file step to a convo. This method will
   * automatically fire up a WebSocket if necessary and close it when finished.
   * It also automatically adds data sources to the convo.
   *
   * @param convoId
   * @param dsIds
   */
  sendFileStep$(convoId: UUID, dsIds: UUID[]): Observable<Message<Step>> {
    return new Observable(subscriber => {
      const arr$: Observable<any>[] = [];

      for (let dsId of dsIds) {
        arr$.push(this.addDataSource$(convoId, dsId));
      }

      forkJoin([...arr$]).subscribe({
        error: err => {
          subscriber.error(err);
        },
        complete: () => {
          const convo$ = this.startConvo$(convoId).subscribe({
            next: msgs$ => {
              // We need to see two messages come through to know that
              // the step is done:
              //   1. The step with the array of files
              //   2. The manual step in which Moti is telling the user
              //      things are ready
              //
              // We track those here...
              let filesStepRecvd: boolean = false;
              let readyStepRecvd: boolean = false;

              const unsubDelay = 5000;

              msgs$.pipe(
                filter((msg: Message<Step>) => {
                  if (msg._ws.funcName === FuncConvoNewStep) {
                    const step: Step = msg.payload;

                    if (!filesStepRecvd) {
                      const stepDsIds: UUID[] = step.files?.map(f => f.dataSourceId) ?? [];
                      // See if the sources in the step match dsIds
                      filesStepRecvd = dsIds.length === stepDsIds.length && dsIds.every((element, index) => element === stepDsIds[index]);
                    }

                    if (!readyStepRecvd) {
                      readyStepRecvd = step.config?.provider === Provider.MANUAL && step.createdByProvider === true;
                    }
                  }

                  return filesStepRecvd && readyStepRecvd;
                }),
                take(1),
              ).subscribe({
                next: msg => {
                  subscriber.next(msg);

                  setTimeout(() => {
                    convo$.unsubscribe();
                  }, unsubDelay);
                },
                error: err => {
                  subscriber.error(err);

                  setTimeout(() => {
                    convo$.unsubscribe();
                  }, unsubDelay);
                },
                complete: () => {
                  setTimeout(() => {
                    convo$.unsubscribe();
                  }, unsubDelay);
                },
              });

              this.sendFileStep(convoId, dsIds);
            },
            error: err => {
              subscriber.error(err);
            },
            complete: () => {
              subscriber.complete();
            },
          });
        },
      });
    });
  }


  sendStepDeletion(stepId: StepId, convoId: ConvoId) {
    this.scrollToBubbleGroup = true;

    const msg: StepDeletionMessage = {
      _ws: {
        funcName: FuncConvoStepDeletion,
      },
      payload: {
        stepId,
        convoId,
      },
    };

    this.sendMessage(convoId, msg);
  }


  sendStepUpdate(stepId: StepId, convoId: ConvoId, isAlternate: false | null = null) {
    this.scrollToBubbleGroup = false;

    const msg: StepUpdateMessage = {
      _ws: {
        funcName: FuncConvoStepUpdate,
      },
      payload: {
        stepId,
        isAlternate,
      },
    };

    this.sendMessage(convoId, msg);
  }


  sendStepRegen(stepId: StepId, convoId: ConvoId) {
    this.scrollToBubbleGroup = false;

    const msg: StepRegenMessage = {
      _ws: {
        funcName: FuncConvoStepRegen,
      },
      payload: {
        stepId: stepId,
        convoId: convoId,
      },
    };

    this.sendMessage(convoId, msg);
  }


  sendStepGroupAddition(primaryStepId: StepId, convoId: ConvoId, config: StepConfig) {
    this.scrollToBubbleGroup = false;

    const msg: StepGroupAdditionMessage = {
      _ws: {
        funcName: FuncConvoStepGroupAddition,
      },
      payload: {
        primaryStepId,
        config,
      },
    };

    this.sendMessage(convoId, msg);
  }


  private sendMessage(convoId: ConvoId, msg: Message) {
    this.sockets$[convoId].next(msg);
  }


  /**
   * Finds an existing convo for a specific topic
   * @param topicId - The ID of the topic to find a convo for
   * @returns Observable with the found convo or null if none exists
   */
  findConvoForTopic$(topicId: string): Observable<Convo | null> {
    return this.queryConvos$({ topicId }).pipe(
      map(convos => convos.length > 0 ? convos[0] : null)
    );
  }

  /**
   * Creates a convo for a topic based on the API contract
   * @param topicId - The ID of the topic to create a convo for
   * @param name - The name for the conversation
   * @returns Observable with the created convo or error with existing convo in 409 response
   */
  createConvoForTopic$(topicId: string, name: string): Observable<Convo> {
    const body = { name, topicId }
    return this.http.post<Convo>(`${this.httpHost}/convos`, body);
  }

  createConvo$(owner: MotiGrantee, name: string): Observable<Convo> {
    const convo: Convo = {
      name,
      createdBy: this.sess.user.ID,
    };

    let params = new HttpParams();

    switch (owner.type) {
      case FgaType.topic:
        params = params.set('topicId', owner.id);
        break;
      case FgaType.team:
        params = params.set('teamId', owner.id);
        break;
      case FgaType.user:
        params = params.set('userId', owner.id);
        break;
      default:
        throw new Error('invalid grantee type for convo');
    }

    return new Observable<Convo>(subscriber => {
      this.http.post<Convo>(`${this.httpHost}/convos`, convo, {
        params,
      }).subscribe({
        next: convo => {
          subscriber.next(convo);
        },
        error: err => {
          subscriber.error(err);
        },
        complete: () => {
          subscriber.complete();
        },
      });
    });
  }


  convo$Map: { [convoId: ConvoId]: ReplaySubject<Convo> } = {};


  getConvo$(convoId: ConvoId): Observable<Convo> {
    return this.wrapAndCache(
      this.convo$Map,
      convoId,
      obs$ => {
        this.http.get<Convo>(`${this.httpHost}/convos/${convoId}`).subscribe({
          next: convo => {
            obs$.next(convo);
            obs$.complete();
          },
          error: err => {
            obs$.error(err);
          },
        });
      },
      10,
    );
  }


  queryConvos$(query: ConvosQuery, includeLatestStep: boolean = false): Observable<Convo[]> {
    return new Observable<Convo[]>(subscriber => {
      this.http.get<Convo[]>(`${this.httpHost}/convos`, {
        params: {
          ...query,
          includeLatestStep: includeLatestStep ? '1' : '',
        },
      }).subscribe({
        next: convos => {
          subscriber.next(convos);
          subscriber.complete();
        },
        error: err => {
          subscriber.error(err);
        },
      });
    });
  }


  searchSteps$(text: string): Observable<Step[]> {
    return this.http.get<Step[]>(`${this.httpHost}/steps`, {
      params: {
        searchTerm: text,
      },
    });
  }


  getSteps$(convoId: ConvoId, after?: MotiTimestamp): Observable<Step[]> {
    return this.http.get<Step[]>(`${this.httpHost}/convos/${convoId}/steps`, {
      params: after ? {
        after: after,
      } : null,
    });
  }


  patchStep$(stepId: StepId, isAlternate: boolean): Observable<any> {
    return this.http.patch(`${this.httpHost}/steps/${stepId}`, {
      isAlternate,
    });
  }


  deleteStep$(stepId: StepId): Observable<any> {
    return this.http.delete(`${this.httpHost}/steps/${stepId}`);
  }


  getContributorIds$(convoId: ConvoId): Observable<UserId[]> {
    return this.http.get<UUID[]>(`${this.httpHost}/convos/${convoId}/users`, {
      params: {
        contributors: '1',
      },
    });
  }


  getAllUserIds$(convoId: ConvoId): Observable<UserId[]> {
    return this.http.get<UUID[]>(`${this.httpHost}/convos/${convoId}/users`, {
      params: {
        all: '1',
      },
    });
  }


  addDataSource$(convoId: UUID, dataSourceId: UUID): Observable<any> {
    return this.http.post<any>(`${this.httpHost}/convos/${convoId}/data-sources/${dataSourceId}`, null);
  }


  removeDataSource$(convoId: UUID, dataSourceId: UUID): Observable<any> {
    return this.http.delete(`${this.httpHost}/convos/${convoId}/data-sources/${dataSourceId}`);
  }

}
